import React, { useEffect, useCallback, useState, useMemo } from "react";
import VendedorTable from "../components/VendedorTable";
import ClienteDialog from "../components/ClienteDialog";
import SeguimientoContainer from "./SeguimientoContainer";
import socket from "../../socket";
import { apiFetch } from "../../api";
import { getUserContext } from "../../auth";
import PuntuacionDialog from "../../Admin/components/PuntuacionDialog";
import ProgramarFuturoDialog from "../components/ProgramarFuturoDialog";
import ConfirmVentaDialog from "../components/ConfirmVentaDialog";
import { EXCLUSIVE_FILTERS } from "./Principal";
import DuplicateResolveDialog from "../../Admin/components/DuplicateResolveDialog";
import EditConflictTransferDialog from "../../Admin/components/EditConflictTransferDialog";

// Hook personalizado para debounce
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
}

const ESTADIOS_ORDER = [
  "INICIO",
  "INFORMACION",
  "PRECIO",
  "NEGOCIACION",
  "SEGUIMIENTO",
  "INTERES",
];

const UI = {
  ESTADIOS: [
    { key: "INICIO", label: "Inicio", color: "#d9ebfd" },
    { key: "INFORMACION", label: "Información", color: "#cfeefd" },
    { key: "PRECIO", label: "Precio", color: "#daf5f8" },
    { key: "NEGOCIACION", label: "Negociación", color: "#fffbd4" },
    { key: "SEGUIMIENTO", label: "Seguimiento", color: "#fff1c8" },
    { key: "INTERES", label: "Interés", color: "#fee8c7" },
  ],
  ESTADOS: ["ACTIVO", "VENTA", "BAJA", "FUTURO"],
  COLORS: { estadoHeader: "#e0eed7", derechaBg: "#f6f7f9", bodyBg: "#fff" },
  WIDTHS: { estadioCol: 120 },
};

const idFromRow = (r) => r?.id_ven_cli ?? r?.id;
const clienteIdFromRow = (r) =>
  r?.id_cliente ?? r?.cliente?.id ?? r?.cliente_id ?? r?.clienteId;
const estadioActualFromRow = (r) => r?.estadioActual ?? r?.estadio ?? "INICIO";

const normalizeEstado = (e) => {
  const s = String(e ?? "").toUpperCase();
  if (s.endsWith("ACTIVO")) return "ACTIVO";
  if (s.endsWith("VENTA")) return "VENTA";
  if (s.endsWith("BAJA")) return "BAJA";
  if (s.endsWith("FUTURO")) return "FUTURO";
  return s;
};

export default function TablaContainer({
  vendedorId,
  exclusiveFilter,
  selectedStates,
  onStateCountsChange = () => {},
  sortOrder = "desc",
  onEstadoUpdated = () => {},
  searchTerm = "",
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Debounce del término de búsqueda (300ms)
  const debouncedSearch = useDebounce(searchTerm, 300);
  const selectedStatesKey = useMemo(
    () => (Array.isArray(selectedStates) ? selectedStates.join("|") : ""),
    [selectedStates]
  );

  // Paginación
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(15);

  const [openSeg, setOpenSeg] = useState(false);
  const [segAsign, setSegAsign] = useState(null);

  // Puntuación (VENTA/BAJA)
  const [openPunt, setOpenPunt] = useState(false);
  const [puntAsign, setPuntAsign] = useState(null);

  // Confirmación de VENTA
  const [openConfirmVenta, setOpenConfirmVenta] = useState(false);
  const [ventaAsign, setVentaAsign] = useState(null);

  // Futuro (Programación)
  const [openFuturo, setOpenFuturo] = useState(false);
  const [futuroAsign, setFuturoAsign] = useState(null);
  const [futuroLoading, setFuturoLoading] = useState(false);

  const handleOpenSeguimiento = (row) => {
    setSegAsign(row);
    setOpenSeg(true);
  };

  const [selectedId, setSelectedId] = useState(null);

  const [openClient, setOpenClient] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientSaving, setClientSaving] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicatePayload, setDuplicatePayload] = useState(null);
  const [editConflictOpen, setEditConflictOpen] = useState(false);
  const [editConflictPayload, setEditConflictPayload] = useState(null);


  const [edgeHitRowId, setEdgeHitRowId] = useState(null);
  const triggerEdgeHit = useCallback((rowId) => {
    setEdgeHitRowId(rowId);
    setTimeout(() => setEdgeHitRowId(null), 220);
  }, []);

  const buildQSFiltered = useCallback(() => {
    const params = new URLSearchParams();
    if (
      Array.isArray(selectedStates) &&
      selectedStates.length > 0 &&
      selectedStates.length < 4
    ) {
      params.set(
        "estados",
        selectedStates.map((s) => String(s).toUpperCase()).join(",")
      );
    }
    if (sortOrder === "asc") params.set("orden", "0");
    if (sortOrder === "desc") params.set("orden", "1");
    if (exclusiveFilter === EXCLUSIVE_FILTERS.NUEVOS) {
      params.set("tipo_filtro", "NUEVOS");
    }
    if (exclusiveFilter === EXCLUSIVE_FILTERS.ATRASADOS) {
      params.set("tipo_filtro", "ATRASADOS");
    }

    // Paginación (API base 1)
    params.set("page", String(page + 1));
    params.set("limit", String(limit));

    // Búsqueda
    if (debouncedSearch && debouncedSearch.trim()) {
      params.set("search", debouncedSearch.trim());
    }

    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }, [selectedStates, sortOrder, page, limit, debouncedSearch, exclusiveFilter]);

  const reqSeqRef = React.useRef(0);

  // 1. Fetch de la tabla paginada
  const fetchAsignaciones = useCallback(async () => {
    const seq = ++reqSeqRef.current;

    setLoading(true);
    setError(null);
    try {
      const qs = buildQSFiltered();
      const res = await apiFetch(`/api/vendedores/${vendedorId}/asignaciones${qs}`);

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // Si llegó tarde, ignorar
      if (seq !== reqSeqRef.current) return;

      setRows(Array.isArray(data.items) ? data.items : []);
      setTotal(data.total || 0);
    } catch (e) {
      if (seq !== reqSeqRef.current) return;
      console.error(e);
      setError("No se pudieron cargar las asignaciones.");
      setRows([]);
    } finally {
      if (seq === reqSeqRef.current) setLoading(false);
    }
  }, [vendedorId, buildQSFiltered]);

  // 2. Fetch de contadores totales (separado para no romper paginación)
  const fetchCounts = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/vendedores/${vendedorId}/estados_count`);
      if (res.ok) {
        const counts = await res.json();
        const normalizedCounts = {
          ACTIVO: counts.ACTIVO || 0,
          VENTA: counts.VENTA || 0,
          BAJA: counts.BAJA || 0,
          FUTURO: counts.FUTURO || 0,
        };
        onStateCountsChange(normalizedCounts);
      }
    } catch (e) {
      console.error("Error fetching state counts", e);
    }
  }, [vendedorId, onStateCountsChange]);

  const reloadAsignaciones = useCallback(async () => {
    // Cargamos tabla y contadores en paralelo
    await Promise.all([fetchAsignaciones(), fetchCounts()]);
  }, [fetchAsignaciones, fetchCounts]);

  const openRegistrarVentaTab = useCallback((idVenCli, row = null) => {
    if (!idVenCli) return;
    const path = `/ventas/tablero/registrar-venta/${idVenCli}`;

    try {
      const prefill = {
        id_ven_cli: Number(idVenCli),
        id_cliente: row?.id_cliente ?? row?.cliente?.id ?? null,
        cliente_nombre: row?.clienteNombre ?? row?.nombreCliente ?? null,
        vehiculo_consultado: row?.consulta ?? row?.vehiculo_consultado ?? null,
      };
      localStorage.setItem(`venta_prefill_${idVenCli}`, JSON.stringify(prefill));
    } catch (error) {
      console.error("No se pudo preparar el prefill de venta", error);
    }

    const newTab = window.open("", "_blank");

    if (!newTab) {
      alert("No se pudo abrir la pestaña. Verifica el bloqueador de ventanas emergentes.");
      return;
    }

    try {
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (!key) continue;
        const value = sessionStorage.getItem(key);
        if (value !== null) {
          newTab.sessionStorage.setItem(key, value);
        }
      }
    } catch (error) {
      console.error("No se pudo clonar la sesión en la nueva pestaña", error);
    }

    newTab.location.href = path;
  }, []);

  useEffect(() => {
    reloadAsignaciones();
  }, [reloadAsignaciones]);

  // Refresca la tabla cuando otra pestaña registra una venta.
  useEffect(() => {
    const refreshAfterVenta = async () => {
      await reloadAsignaciones().catch(() => {});
      onEstadoUpdated();
    };

    const handleChannelMessage = (event) => {
      const data = event?.data;
      if (data?.type !== "venta-registrada") return;
      refreshAfterVenta();
    };

    const handleStorage = (event) => {
      if (event.key !== "venta_registrada_event" || !event.newValue) return;
      try {
        const data = JSON.parse(event.newValue);
        if (data?.type !== "venta-registrada") return;
        refreshAfterVenta();
      } catch (e) {
        // noop
      }
    };

    let channel = null;
    if (typeof window !== "undefined" && "BroadcastChannel" in window) {
      channel = new BroadcastChannel("ventas-channel");
      channel.onmessage = handleChannelMessage;
    }

    window.addEventListener("storage", handleStorage);
    return () => {
      if (channel) channel.close();
      window.removeEventListener("storage", handleStorage);
    };
  }, [reloadAsignaciones, onEstadoUpdated]);

  // Resetear página cuando cambia la búsqueda
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, sortOrder, selectedStatesKey, exclusiveFilter]);

  // Handlers de paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = useMemo(() => {
    if (!rows?.length) return [];
    const selectedSet = new Set(
      (selectedStates || []).map((s) => normalizeEstado(s))
    );
    return rows.filter((r) =>
      selectedSet.has(
        normalizeEstado(r?.estado ?? r?.estadoVenta ?? r?.estado_venta)
      )
    );
  }, [rows, selectedStates]);

  // Sockets logic
  useEffect(() => {
    const handleChangeEstadio = async (data) => {
      const user = getUserContext();
      if (String(data.empresa_id) !== String(user.empresa_id)) return;
      const socketUserId = data.user_id;
      const currentUserId = user?.id;
      if (String(socketUserId) === String(currentUserId))
        await reloadAsignaciones();
    };
    const handleUpdateEstadoLead = async (data) => {
      const user = getUserContext();
      if (String(data.empresa_id) !== String(user.empresa_id)) return;
      const socketVendedorId = data.id || data.vendedor_id;
      if (String(socketVendedorId) === String(vendedorId)) {
        await reloadAsignaciones();
        onEstadoUpdated();
      }
    };
    const handleCreateAsignacion = (data) => {
      const user = getUserContext();
      if (String(data.empresa_id) !== String(user.empresa_id)) return;
      const socketVendedorId = data.vendedor_id;
      if (String(socketVendedorId) === String(vendedorId)) {
        reloadAsignaciones();
        onEstadoUpdated();
      }
    };

    socket.on("create_asignacion", handleCreateAsignacion);
    socket.on("update_estado_lead", handleUpdateEstadoLead);
    socket.on("change_estadio", handleChangeEstadio);

    const handleCreateCliente = (data) => {
      try {
        const user = getUserContext();
        if (String(data.empresa_id) !== String(user.empresa_id)) return;
        const socketVendedorId = data.vendedor_id ?? data.vendedorId ?? null;
        if (
          socketVendedorId == null ||
          String(socketVendedorId) === String(vendedorId)
        ) {
          reloadAsignaciones();
          onEstadoUpdated();
        }
      } catch (err) {
        reloadAsignaciones();
      }
    };
    const handleUpdateCliente = (data) => {
      try {
        const user = getUserContext();
        if (String(data.empresa_id) !== String(user.empresa_id)) return;
        reloadAsignaciones();
      } catch (err) {
        reloadAsignaciones();
      }
    };
    const handleDeleteCliente = (data) => {
      try {
        const user = getUserContext();
        if (String(data.empresa_id) !== String(user.empresa_id)) return;
        reloadAsignaciones();
      } catch (err) {
        reloadAsignaciones();
      }
    };

    socket.on("create_cliente", handleCreateCliente);
    socket.on("update_cliente", handleUpdateCliente);
    socket.on("delete_cliente", handleDeleteCliente);

    const handleWindowAssigned = (ev) => {
      try {
        const detail = ev?.detail || {};
        const vendedorAssigned =
          detail?.vendedorId ?? detail?.vendedor_id ?? null;
        if (
          vendedorAssigned == null ||
          String(vendedorAssigned) === String(vendedorId)
        ) {
          reloadAsignaciones();
          onEstadoUpdated();
        }
      } catch (err) {
        reloadAsignaciones();
      }
    };
    const handleOpenSeguimientoFromLead = (ev) => {
      try {
        const detail = ev?.detail || {};
        const vendedorAssigned = detail?.id_vendedor ?? detail?.vendedor_id ?? detail?.vendedorId ?? null;
        if (
          vendedorAssigned == null ||
          String(vendedorAssigned) === String(vendedorId)
        ) {
          setSegAsign(detail);
          setOpenSeg(true);
          reloadAsignaciones();
          onEstadoUpdated();
        }
      } catch (err) {
        console.error("Error abriendo seguimiento desde lead", err);
      }
    };
    window.addEventListener("created_cliente_assigned", handleWindowAssigned);
    window.addEventListener("open_seguimiento_asignacion", handleOpenSeguimientoFromLead);

    return () => {
      socket.off("create_asignacion", handleCreateAsignacion);
      socket.off("update_estado_lead", handleUpdateEstadoLead);
      socket.off("change_estadio", handleChangeEstadio);
      window.removeEventListener(
        "created_cliente_assigned",
        handleWindowAssigned
      );
      window.removeEventListener(
        "open_seguimiento_asignacion",
        handleOpenSeguimientoFromLead
      );
      socket.off("create_cliente", handleCreateCliente);
      socket.off("update_cliente", handleUpdateCliente);
      socket.off("delete_cliente", handleDeleteCliente);
    };
  }, [vendedorId, reloadAsignaciones, onEstadoUpdated]);

  // Handlers de acciones
  const handleEstadoChange = useCallback(
    async (row, nuevoEstado) => {
      const rowId = idFromRow(row);
      const norm = normalizeEstado(nuevoEstado);

      // Si el nuevo estado es FUTURO -> Abrir ProgramarFuturoDialog
      if (norm === 'FUTURO') {
        setFuturoAsign({ id: rowId, row, pendingEstado: norm });
        setOpenFuturo(true);
        return;
      }

      // Si el nuevo estado es VENTA -> confirmar acción
      if (norm === "VENTA") {
        setVentaAsign({ id: rowId, row, pendingEstado: norm });
        setOpenConfirmVenta(true);
        return;
      }

      try {
        // Si volvemos a ACTIVO, aplicarlo inmediatamente
        if (norm === "ACTIVO") {
          setRows((prev) =>
            prev.map((r) =>
              idFromRow(r) === rowId
                ? { ...r, estado: norm, estadoVenta: norm }
                : r
            )
          );
          const res = await apiFetch(`/api/leads/${rowId}/estado`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ estado: norm }),
            credentials: "include",
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          onEstadoUpdated();
          fetchCounts(); // Actualizar contadores tras cambio de estado
          return;
        }

        // Para BAJA: abrir diálogo de puntuación y almacenar estado pendiente
        setPuntAsign({ id: rowId, row, pendingEstado: norm });
        setOpenPunt(true);
      } catch (error) {
        alert("Error al actualizar el estado. Inténtalo de nuevo.");
      }
    },
    [onEstadoUpdated, fetchCounts]
  );

  //  LÓGICA DE FUTURO 
  const programarAFuturo = async (payload) => {
    if (!futuroAsign?.id) return false;
    try {
      const res = await apiFetch(`/api/leads/${futuroAsign.id}/programar-futuro`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Error al mover a futuro");
      return true;
    } catch (e) {
      console.error("Error moviendo a Futuro:", e);
      alert("Error al mover el cliente a Futuro.");
      return false;
    }
  };

  const handleProgramarFuturo = async (notifData) => {
    setFuturoLoading(true);
    try {
      const payload = {
        ...notifData,
        crear_notificacion: true,
      };

      const ok = await programarAFuturo(payload);
      if (ok) {
        // Actualizar UI localmente
        setRows((prev) => prev.filter((r) => idFromRow(r) !== futuroAsign.id));
        fetchCounts();
        onEstadoUpdated();
        setOpenFuturo(false);
        setFuturoAsign(null);
      }

    } catch (e) {
      console.error(e);
      alert("Ocurrió un error al programar.");
    } finally {
      setFuturoLoading(false);
    }
  };

  const handleIgnorarFuturo = async () => {
    setFuturoLoading(true);
    try {
      const ok = await programarAFuturo({ crear_notificacion: false });
      if (ok) {
        setRows((prev) => prev.filter((r) => idFromRow(r) !== futuroAsign.id));
        fetchCounts();
        onEstadoUpdated();
        setOpenFuturo(false);
        setFuturoAsign(null);
      }
    } finally {
      setFuturoLoading(false);
    }
  };

  // LÓGICA DE PUNTUACIÓN
  const handleSubmitPuntuacion = async (score) => {
    if (!puntAsign?.id) {
      setOpenPunt(false);
      setPuntAsign(null);
      return;
    }
    try {
      // Llamada atómica al backend que guarda puntuación y cambia estado
      const payload = {
        estado: puntAsign.pendingEstado ?? "VENTA",
        puntuacion_venta: Number(score),
      };
      const res = await apiFetch(`/api/asignaciones/${puntAsign.id}/cerrar`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json().catch(() => null);
      if (data) {
        setRows((prev) =>
          prev.map((r) =>
            idFromRow(r) === puntAsign.id
              ? {
                  ...r,
                  puntuacion_venta: data.puntuacion_venta ?? Number(score),
                  estado: data.estado ?? puntAsign.pendingEstado ?? r.estado,
                }
              : r
          )
        );
        onEstadoUpdated();
        fetchCounts();
      }
    } catch (e) {
      console.error("Error enviando puntuación y cerrando asignación:", e);
      // En caso de error, recargar para consistencia
      await reloadAsignaciones().catch(() => {});
    } finally {
      setOpenPunt(false);
      setPuntAsign(null);
    }
  };

  const handleVentaConfirmAction = async (action) => {
    if (!ventaAsign?.id) {
      setOpenConfirmVenta(false);
      setVentaAsign(null);
      return;
    }

    if (action === "cancel") {
      setOpenConfirmVenta(false);
      setVentaAsign(null);
      return;
    }

    if (action === "registrar") {
      openRegistrarVentaTab(ventaAsign.id, ventaAsign.row);
      setOpenConfirmVenta(false);
      setVentaAsign(null);
      return;
    }

    if (action === "ignorar") {
      setPuntAsign({
        id: ventaAsign.id,
        row: ventaAsign.row,
        pendingEstado: "VENTA",
      });
      setOpenConfirmVenta(false);
      setVentaAsign(null);
      setOpenPunt(true);
    }
  };

  const changeEstadioById = useCallback(
    async (id, targetEstadio, originalEstadio = "INICIO") => {
      if (!id) return;
      setRows((prev) =>
        prev.map((r) =>
          (r.id_ven_cli ?? r.id) === id
            ? { ...r, estadioActual: targetEstadio }
            : r
        )
      );
      try {
        const res = await apiFetch(`/api/estadio/${id}/cambiar`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ estadio: targetEstadio }),
          credentials: "include",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setRows((prev) =>
          prev.map((r) =>
            (r.id_ven_cli ?? r.id) === id
              ? { ...r, estadioActual: data.estadio ?? targetEstadio }
              : r
          )
        );
      } catch (err) {
        console.error("Error cambiando estadio:", err);
        setRows((prev) =>
          prev.map((r) =>
            (r.id_ven_cli ?? r.id) === id
              ? { ...r, estadioActual: originalEstadio }
              : r
          )
        );
      }
    },
    []
  );

  const handleDropToEstadio = (targetEstadio, ev) => {
    ev.preventDefault();
    try {
      let raw = ev.dataTransfer.getData("application/json");
      let payload = null;
      if (raw) {
        try {
          payload = JSON.parse(raw);
        } catch {
          payload = null;
        }
      }
      if (!payload) {
        raw = ev.dataTransfer.getData("text/plain");
        if (raw) payload = { id: raw };
      }
      if (!payload) return;
      const id = payload.id;
      const original = payload.from;
      if (!id) return;
      if (original === targetEstadio) return;
      changeEstadioById(id, targetEstadio, original ?? "INICIO");
    } catch (err) {
      console.error("drop parse error", err);
    }
  };

  const handleOpenCliente = async (row) => {
    const clienteId = clienteIdFromRow(row);
    if (!clienteId) return;
    setClientLoading(true);
    setClientData(null);
    setOpenClient(true);
    try {
      const res = await apiFetch(`/api/clientes/${clienteId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setClientData(data);
    } catch {
      setClientData(null);
    } finally {
      setClientLoading(false);
    }
  };
  const handleSaveClient = async (cliente) => {
    if (!cliente || !cliente.id) return null;
    setClientSaving(true);
    try {
      const payload = {
        nombre: cliente.nombre,
        telefono: cliente.telefono || null,
        email: cliente.email || null,
        obtenido_de: cliente.obtenido_de || null,
        observacion: cliente.observacion || null,
      };
      const res = await apiFetch(`/api/clientes/${cliente.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });
      if (!res.ok) {
        if (res.status === 409) {
          let json = null;
          try { json = await res.json(); } catch (e) { json = null; }

          if (json && json.origin === 'edit') {
            setEditConflictPayload(json);
            setEditConflictOpen(true);
          } else {
            setDuplicatePayload(json);
            setDuplicateOpen(true);
          }

          setClientSaving(false);
          return null;
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const updated = await res.json();
      setRows((prev) =>
        prev.map((r) => {
          const cid = clienteIdFromRow(r);
          if (String(cid) === String(updated.id)) {
            return {
              ...r,
              clienteNombre: updated.nombre ?? r.clienteNombre,
              nombreCliente: updated.nombre ?? r.nombreCliente,
            };
          }
          return r;
        })
      );
      setClientData(updated);
      return updated;
    } catch (err) {
      console.error("Error saving cliente:", err);
      throw err;
    } finally {
      setClientSaving(false);
    }
  };

  // Teclado
  useEffect(() => {
    if (!selectedId) return;
    const onKey = (ev) => {
      if (!["ArrowLeft", "ArrowRight", "Escape"].includes(ev.key)) return;
      ev.preventDefault();
      if (ev.key === "Escape") {
        setSelectedId(null);
        return;
      }
      const row = rows.find((r) => (r.id_ven_cli ?? r.id) === selectedId);
      if (!row) return;
      const current = estadioActualFromRow(row);
      const idx = ESTADIOS_ORDER.indexOf(current);
      if (idx === -1) return;
      const targetIdx = ev.key === "ArrowLeft" ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= ESTADIOS_ORDER.length) {
        triggerEdgeHit(selectedId);
        return;
      }
      const targetEstadio = ESTADIOS_ORDER[targetIdx];
      changeEstadioById(selectedId, targetEstadio, estadioActualFromRow(row));
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selectedId, rows, changeEstadioById, triggerEdgeHit]);

  return (
    <>
      <VendedorTable
        rows={visibleRows}
        loading={loading}
        error={error}
        ui={UI}
        readOnly={false}
        edgeHitRowId={edgeHitRowId}
        selectedAssignmentId={selectedId}
        onSelectAssignment={(row) => {
          const id = row.id_ven_cli ?? row.id;
          setSelectedId((prev) => (prev === id ? null : id));
        }}
        onEstadoChange={handleEstadoChange}
        onOpenSeguimiento={handleOpenSeguimiento}
        onDropToEstadio={handleDropToEstadio}
        onOpenCliente={handleOpenCliente}
        // Props de paginación
        count={total}
        page={page}
        rowsPerPage={limit}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <SeguimientoContainer
        open={Boolean(openSeg && segAsign)}
        onClose={() => {
          setOpenSeg(false);
          setSegAsign(null);
        }}
        asignacion={segAsign ?? null}
        adminReadOnly={false}
      />

      <ClienteDialog
        open={openClient}
        onClose={() => setOpenClient(false)}
        cliente={clientData}
        onChange={setClientData}
        onSave={handleSaveClient}
        onOpenSeguimiento={handleOpenSeguimiento}
        saving={clientSaving}
        loading={clientLoading}
      />
      <DuplicateResolveDialog
        open={duplicateOpen}
        onClose={() => setDuplicateOpen(false)}
        existing={duplicatePayload?.existing}
        incoming={duplicatePayload?.incoming}
        match_source={duplicatePayload?.match_source}
        onResolved={({ kept, created }) => {
          setDuplicateOpen(false);
          setOpenClient(false);
        }}
      />
      <EditConflictTransferDialog
        open={editConflictOpen}
        onClose={() => { setEditConflictOpen(false); setEditConflictPayload(null); }}
        existing={editConflictPayload?.existing}
        incoming={editConflictPayload?.incoming}
        resolution={editConflictPayload?.resolution}
        target={clientData}
        onResolved={({ kept, created }) => {
          setEditConflictOpen(false);
          setEditConflictPayload(null);
          setOpenClient(false);
        }}
      />
      <PuntuacionDialog
        open={openPunt}
        initial={puntAsign?.row?.puntuacion_venta ?? 0}
        onClose={() => {
          setOpenPunt(false);
          setPuntAsign(null);
        }}
        onSubmit={handleSubmitPuntuacion}
      />
      <ConfirmVentaDialog
        open={openConfirmVenta}
        cliente={ventaAsign?.row}
        onAction={handleVentaConfirmAction}
        onClose={() => {
          setOpenConfirmVenta(false);
          setVentaAsign(null);
        }}
      />
      <ProgramarFuturoDialog
        open={openFuturo}
        loading={futuroLoading}
        onClose={() => { setOpenFuturo(false); setFuturoAsign(null); }}
        onConfirm={handleProgramarFuturo}
        onIgnore={handleIgnorarFuturo}
        cliente={futuroAsign?.row}
      />
    </>
  );
}
